import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { HiCheck } from "react-icons/hi";

import { SELECTED_LANGUAGE_PREFIX } from "../../../../constants";
import { useAppState } from "../../../../context/StateContext";
import { Languages } from "../../../../models/languages";
import LanguagesService from "../../../../services/languages.service";
import UserService from "../../../../services/user.service";
import { LanguagesUsage, Status } from "../../../../util/enums";
import useStyles from "../styles";
import SkeletonLanguages from "./SkeletonLanguages";

interface LanguagesListDropdownProps {
  handleClose: () => void;
}

const LanguagesListDropdown = ({ handleClose }: LanguagesListDropdownProps) => {
  const [languages, setLanguages] = useState<Languages[]>([]);
  const [orderLanguages, setOrderLanguages] = useState<Languages[]>([]);

  const classes = useStyles();

  const { activeLanguageId, setActiveLanguageId, setDefaultLanguage, setActiveFlag, currentUser } =
    useAppState();

  const handleSetActiveLanguage = (language: Languages) => {
    if (language.id) setActiveLanguageId(language.id);
    if (language.defaultLanguageId) setDefaultLanguage(language.defaultLanguageId);
    setActiveFlag(language.flag);
    if (language.prefix) localStorage.setItem(SELECTED_LANGUAGE_PREFIX, language.prefix);

    if (currentUser?.id && language.id) {
      UserService.update(currentUser.id, { translateId: language.id } as any).catch(() => {});
    }

    handleClose();
    window.location.reload();
  };

  const { isFetching } = useQuery(
    ["languages.translates"],
    () =>
      LanguagesService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        usage: LanguagesUsage.WEB,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      onSuccess: (response: AxiosResponse<Languages[]>) => {
        if (response && response?.data) {
          setLanguages(response?.data);
        }
      },
      enabled: !!currentUser?.companyId,
    }
  );

  useEffect(() => {
    if (activeLanguageId !== undefined && languages.length > 1) {
      const indexOfActiveLanguage = languages.findIndex(
        (language) => language.id === activeLanguageId
      );
      const activeLanguage = languages[indexOfActiveLanguage];

      if (indexOfActiveLanguage > 0) {
        const languagesAux = languages;

        languagesAux.splice(indexOfActiveLanguage, 1);
        languagesAux.unshift(activeLanguage);

        setOrderLanguages(languagesAux);
      } else {
        setOrderLanguages(languages);
      }
    } else if (languages.length) {
      setOrderLanguages(languages);
    }
  }, [activeLanguageId, languages]);

  return (
    <Grid container direction="column">
      {isFetching || !orderLanguages.length ? (
        <>
          <SkeletonLanguages />
          <SkeletonLanguages />
          <SkeletonLanguages />
        </>
      ) : (
        orderLanguages &&
        orderLanguages?.map((language) => (
          <div
            key={language?.id}
            className={classes.languageSelect}
            onClick={() => handleSetActiveLanguage(language)}
          >
            <div
              className={
                language.id === activeLanguageId
                  ? classes.languageSelectContainerSelected
                  : classes.languageSelectContainer
              }
            >
              <img
                loading="lazy"
                className={classes.flagImage}
                src={
                  language?.flag
                    ? `https://flagcdn.com/w80/${language?.flag.toLowerCase()}.png`
                    : ""
                }
                srcSet={
                  language?.flag
                    ? `https://flagcdn.com/w160/${language?.flag.toLowerCase()}.png 2x`
                    : ""
                }
                alt=""
              />
              <span
                className={
                  language.id === activeLanguageId
                    ? classes.languageDescriptionSelected
                    : classes.languageDescription
                }
              >
                {language?.name}
              </span>
              {language.id === activeLanguageId && (
                <HiCheck color="#308CDB" size={20} className={classes.checkIcon} />
              )}
            </div>
          </div>
        ))
      )}
    </Grid>
  );
};

export default LanguagesListDropdown;
